import { useState } from "react";
import { usePaymentDetail } from "@features/cx/payments/hooks";
import { PaymentDetail } from "@features/cx/payments/types";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Separator } from "@shared/ui/separator";
import {
  Banknote,
  Calendar,
  Clock,
  CreditCard,
  Eye,
  FileText,
  Receipt,
  Settings,
  UserCheck,
} from "lucide-react";
import { CreditNoteDetailDialog } from "../../../creditNote/creditnotedetails";

interface PaymentInformationCardProps {
  payment: PaymentDetail;
  onViewCreditNote?: (creditNoteId: any) => void;
}

export function PaymentInformationCard({
  payment,
  onViewCreditNote,
}: PaymentInformationCardProps) {
  const [isCreditNoteDetailOpen, setIsCreditNoteDetailOpen] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] = useState<any>(null);

  const { data: paymentData } = usePaymentDetail({
    paymentId: payment?.id,
    remoteUtilityId: payment?.remoteUtilityId,
  });
  
  console.log("paymentData", paymentData);
  
  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case "Posted":
        return "secondary";
      case "Pending":
        return "outline";
      case "Failed":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    if (!method) return <CreditCard className="h-4 w-4" />;

    switch (method.toLowerCase()) {
      case "cash":
        return <Banknote className="h-4 w-4" />;
      case "credit card":
      case "debit card":
        return <CreditCard className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  // Helper function to safely format currency
  const formatCurrency = (value: any): string => {
    if (value === null || value === undefined) return "0.00";
    const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  // Handle credit note dialog opening
  const handleViewCreditNote = () => {
    if (paymentData?.result?.creditNoteDetails) {
      // Transform the credit note data to match the expected format
      const creditNoteData = {
        id: paymentData.result.creditNoteDetails.id,
        transactionId: paymentData.result.creditNoteDetails.transactionId,
        amount: paymentData.result.creditNoteDetails.amount,
        // Add other properties that might be needed by the dialog
        consumerFirstName: payment.consumerFirstName || "",
        consumerLastName: payment.consumerLastName || "",
        consumerNo: payment.consumerNo || "",
        paymentReceivedStatus: payment.paymentReceivedStatus || "",
        paymentPayType: payment.paymentPayType || "",
        paymentDate: payment.paymentDate || "",
        paymentMode: payment.paymentMode || "",
        receiptNo: payment.receiptNo || "",
        createdUserRemoteName: payment.createdUserRemoteName || "",
        remoteUtilityId: payment.remoteUtilityId,
        // Add any other fields that the CreditNoteDetailDialog expects
      };

      setSelectedCreditNote(creditNoteData);
      setIsCreditNoteDetailOpen(true);
    }
    
    // Also call the optional callback prop if provided
    if (onViewCreditNote && paymentData?.result?.creditNoteDetails) {
      onViewCreditNote(paymentData.result.creditNoteDetails.id.toString());
    }
  };

  const isBillPayment = payment?.paymentPayType === "Bill Payment";
  const isServicePayment = payment?.paymentPayType === "Service Payment";

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Information
            </span>
            {payment?.paymentReceivedStatus && (
              <Badge
                variant={getStatusBadgeVariant(payment?.paymentReceivedStatus)}
              >
                {payment?.paymentReceivedStatus}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Amount and Basic Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Amount Paid</p>
              <p className="text-2xl font-bold">${formatCurrency(payment?.amount)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Payment Method</p>
              <div className="flex items-center gap-2">
                {getPaymentMethodIcon(payment?.paymentMode || "")}
                <p className="font-medium">{payment?.paymentMode || "N/A"}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Payment Type</p>
              <p className="font-medium">{payment?.paymentPayType || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Channel</p>
              <p className="font-medium">
                {payment?.paymentChannel || "Walk-in"}
              </p>
            </div>
          </div>

          {/* Bill/Service Specific Details */}
          {(isBillPayment || isServicePayment) && (
            <>
              <Separator />
              <div className="bg-blue-50 p-4 rounded-md">
                <div className="flex items-center gap-2 mb-3">
                  {isBillPayment ? (
                    <Receipt className="h-4 w-4 text-blue-600" />
                  ) : (
                    <Settings className="h-4 w-4 text-blue-600" />
                  )}
                  <span className="font-medium text-blue-700">
                    {isBillPayment
                      ? "Bill Payment Details"
                      : "Service Payment Details"}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {isBillPayment && (
                    <>
                      {payment?.remoteBillId && (
                        <div>
                          <p className="text-muted-foreground">Invoice ID</p>
                          <p className="font-medium">{payment?.billData[0]?.billNumber || "N/A"}</p>
                        </div>
                      )}
                      {payment?.remoteBillId && (
                        <div>
                          <p className="text-muted-foreground">Bill Period</p>
                          <p className="font-medium">{payment?.billData[0]?.billMonth || "N/A"}</p>
                        </div>
                      )}
                      {payment?.remoteBillId && (
                        <div>
                          <p className="text-muted-foreground">Bill Amount</p>
                          <p className="font-medium">{payment?.billData[0]?.billAmount || "N/A"}</p>
                        </div>
                      )}
                    </>
                  )}
                  {isServicePayment && (
                    <>
                      {payment?.consumerSupportRequest && (
                        <div>
                          <p className="text-muted-foreground">Service ID</p>
                          <p className="font-medium">
                            {payment?.consumerSupportRequestData?.requestNo || "N/A"}
                          </p>
                        </div>
                      )}
                      {payment?.consumerSupportRequest && (
                        <div>
                          <p className="text-muted-foreground">Service Date</p>
                          <p className="font-medium">
                            {payment?.consumerSupportRequestData?.requestDate || "N/A"}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Amount Comparison */}
          {payment?.extraData?.billAmount && (
            <>
              <Separator />
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="font-medium mb-3">Payment Amount Analysis</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Bill Amount</p>
                    <p className="font-medium">
                      ${formatCurrency(payment?.extraData?.billAmount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Amount Paid</p>
                    <p className="font-medium">${formatCurrency(payment?.amount)}</p>
                  </div>
                  <div>
                    {/* Payment comparison logic can be added here */}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Credit Note Information */}
          {paymentData?.result?.creditNoteDetails && (
            <>
              <Separator />
              <div className="bg-green-50 p-4 rounded-md">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-700">
                      Credit Note Generated
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleViewCreditNote}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View Credit Note
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Credit Note Number</p>
                    <p className="font-medium">{paymentData?.result?.creditNoteDetails?.transactionId}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Credit Amount</p>
                    <p className="font-medium">${formatCurrency(paymentData?.result?.creditNoteDetails?.amount)}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Payment Method Specific Fields */}
          <div className="bg-green-50 p-4 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="h-4 w-4 text-green-600" />
              <span className="font-medium text-green-700">
                 {paymentData?.result?.paymentMode} Details
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Amount Received</p>
                <p className="font-medium">${formatCurrency(payment?.amount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Change Given</p>
                <p className="font-medium">$0.00</p>
              </div>
              <div>
                <p className="text-muted-foreground">Cash Verified</p>
                <p className="font-medium text-green-600">Yes</p>
              </div>
            </div>
          </div>
          <Separator />

          {/* Transaction Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium">Transaction Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transaction ID:</span>
                  <span className="font-mono">
                    {payment?.transactionId || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Receipt Number:</span>
                  <span className="font-mono">{payment?.receiptNo || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div className="flex justify-between w-full">
                    <span className="text-muted-foreground">Payment Date:</span>
                    <span className="font-medium">{payment?.paymentDate || "N/A"}</span>
                  </div>
                </div>
                
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Processing Information</h4>
              <div className="space-y-2 text-sm">
                {payment?.createdUserRemoteName && (
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-muted-foreground" />
                    <div className="flex justify-between w-full">
                      <span className="text-muted-foreground">Received By:</span>
                      <span className="font-medium">
                        {payment?.createdUserRemoteName || "N/A"}
                      </span>
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Processing Time:</span>
                  <span className="font-medium">Immediate</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Verification:</span>
                  <span className="font-medium text-green-600">Verified</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Note Detail Dialog */}
      <CreditNoteDetailDialog
        open={isCreditNoteDetailOpen}
        onClose={() => setIsCreditNoteDetailOpen(false)}
        creditNote={selectedCreditNote}
      />
    </>
  );
}